import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useLang } from "@/lib/i18n.jsx";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const abs = (url) => (url?.startsWith("http") ? url : url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : "");

export function PhotoUploader({ value, onChange, testid = "photo-uploader" }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo immagini"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success(t("dash.toast_uploaded"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.toast_upload_failed")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={testid}>
      {value ? (
        <div className="relative group inline-block">
          <img src={abs(value)} alt="" className="w-40 h-40 object-cover rounded-md border border-border"/>
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-destructive text-destructive-foreground grid place-items-center lift" data-testid={`${testid}-remove`}>
            <X className="w-3.5 h-3.5"/>
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()}
          className="w-40 h-40 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors lift"
          data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-6 h-6 animate-spin"/> : <><Upload className="w-6 h-6 mb-2"/><span className="text-xs text-center px-2">{t("dash.drop_here")}</span></>}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e)=>upload(e.target.files?.[0])} data-testid={`${testid}-input`}/>
    </div>
  );
}

export function MultiPhotoUploader({ values = [], onChange, testid = "multi-uploader" }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      } catch (e) { console.error("photo upload failed", e); }
    }
    onChange([...values, ...urls]);
    setBusy(false);
    if (urls.length) toast.success(`${urls.length} ${t("dash.toast_uploaded").toLowerCase()}`);
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-3">
        {values.map((u, i) => (
          <div key={u} className="relative group">
            <img src={abs(u)} className="w-24 h-24 object-cover rounded-md border border-border"/>
            <button type="button" onClick={()=>remove(i)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground grid place-items-center lift"><X className="w-3 h-3"/></button>
          </div>
        ))}
        <button type="button" onClick={()=>ref.current?.click()}
          className="w-24 h-24 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors lift"
          data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : <><Upload className="w-5 h-5 mb-1"/><span className="text-[10px]">{t("dash.upload_photo")}</span></>}
        </button>
      </div>
      <input ref={ref} type="file" multiple accept="image/*" hidden onChange={(e)=>upload(Array.from(e.target.files||[]))}/>
    </div>
  );
}

export const absUrl = abs;
